import { canSeeElk, elkCampaignUrl } from '@/helpers/elk'
import { formatDate, isDateInPast } from '@/helpers/date'
import { formatCurrency, formatNumber } from '@/helpers/number'
import store from '@/store/index'

const listFilters = localStorage.getItem('order-list-filters')
const sort = localStorage.getItem('order-sort')

const entity = {
  module: 'order',
  key: 'Order',
  permissionKey: 'Order',
  productType: 'Order',
  route: {
    index: 'Orders',
    create: 'CreateOrder',
    edit: 'EditOrder',
  },
  param: 'order',
  singular: 'Order',
  plural: 'Orders',
  statusKey: 'campaignOrderStatusType',
  statusList: ['Archived', 'Converted', 'Draft', 'Pending'],
  list: {
    filters: listFilters ? JSON.parse(listFilters) : [],
    loading: true,
    page: 1,
    pagination: {
      perPage: import.meta.env.DEV ? 10 : 50,
      page: 1,
    },
    search: '',
    shouldRetry: false,
    sortKey: sort ? JSON.parse(sort).key : '',
    sortOrder: sort ? JSON.parse(sort).order : 'asc',
    statusKey: 'campaignOrderStatusType',
    headers: [
      {
        key: 'gap-1',
        pages: [1, 2, 3],
        heading: null,
        size: '1rem',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-hidden',
        classHeader: 'tw-hidden',
      },
      {
        key: 'gap-2',
        pages: [1, 2, 3],
        heading: null,
        size: '1rem',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-hidden',
        classHeader: 'tw-hidden',
      },
      {
        label: 'Status',
        key: 'campaignOrderStatusType',
        pages: [1, 2, 3],
        componentHeader: 'ListHeaderStatus',
        componentList: 'ListCellCampaignToggle',
        size: '60px',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-col-span-3 tw-rounded-tl',
        classHeader: 'tw-col-span-3',
      },
      {
        label: 'Name',
        key: 'name',
        pages: [1, 2, 3],
        componentHeader: 'ListHeaderDefault',
        componentList: 'ListCellEntityName',
        size: 'minmax(100px, 1fr)',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-px-4',
        classHeader: 'tw-pl-4 tw-pr-2',
      },
      {
        label: 'Start date',
        key: 'startDate',
        pages: [1],
        componentHeader: 'ListHeaderDefault',
        componentList: 'ListCellDate',
        size: '150px',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-px-2',
        classHeader: 'tw-px-2',
      },
      {
        label: 'End date',
        key: 'endDate',
        pages: [1],
        componentHeader: 'ListHeaderDefault',
        componentList: 'ListCellDate',
        size: '150px',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-px-2',
        classHeader: 'tw-px-2',
      },
      {
        label: 'Ordered',
        key: 'orderedImpressions',
        pages: [2],
        componentHeader: 'ListHeaderRange',
        componentList: 'ListCellNumber',
        size: '120px',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-px-2 tw-justify-end',
        classHeader: 'tw-px-2 tw-justify-end',
      },
      {
        label: 'Gross CPM',
        key: 'grossCpm',
        pages: [2],
        componentHeader: 'ListHeaderRange',
        componentList: 'ListCellCurrency',
        size: '120px',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-px-2 tw-justify-end',
        classHeader: 'tw-px-2 tw-justify-end',
      },
      {
        label: 'Actions',
        key: 'actions',
        pages: [1, 2, 3],
        componentHeader: 'ListHeaderPagination',
        componentList: 'ListCellActions',
        size: '80px',
        classCell:
          'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-col-span-3 tw-border-l tw-border-r tw-rounded-r',
        classHeader: 'tw-col-span-3',
      },
      {
        key: 'gap-3',
        pages: [1, 2, 3],
        heading: null,
        size: '1rem',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-hidden',
        classHeader: 'tw-hidden',
      },
      {
        key: 'gap-4',
        pages: [1, 2, 3],
        heading: null,
        size: '1rem',
        classCell: 'tw-mt-4 tw-h-28 tablet:tw-h-24 tw-hidden',
        classHeader: 'tw-hidden',
      },
    ],
    filtersCallbacks: {
      accountManagerId(item, accountManagerId) {
        return (
          accountManagerId === null ||
          item.campaignAccountManager?.userId === accountManagerId
        )
      },
      status(item, selectedStatus) {
        return (
          selectedStatus.length === 0 ||
          selectedStatus.includes(item.campaignOrderStatusType)
        )
      },
      saleUserId(item, saleUserId) {
        return (
          saleUserId === null ||
          (item.campaignSaleUsersList || []).some(
            (saleUserList) => saleUserList.userId === saleUserId
          )
        )
      },
      orderedImpressions(item, { min, max }) {
        return (
          (min !== null
            ? parseInt(item.orderedImpressions) >= parseInt(min)
            : true) &&
          (max !== null
            ? parseInt(item.orderedImpressions) <= parseInt(max)
            : true)
        )
      },
      grossCpm(item, { min, max }) {
        return (
          (min !== null
            ? parseFloat(item.grossCpm) >= parseFloat(min)
            : true) &&
          (max !== null ? parseFloat(item.grossCpm) <= parseFloat(max) : true)
        )
      },
    },
    rowOptions: (item) => {
      if (!item) return []

      const options = [
        {
          key: 'copyWorkId',
          label: `Copy Work ID (${item.workId})`,
          iconLabel: `${item.workId}`,
          text: item.workId,
          icon: 'copy',
        },
      ]

      if (!isDateInPast(item.endDate)) {
        options.push(
          {
            key: 'plane',
            label: 'Add Flight',
            to: { name: 'CreateOrderFlight', params: { order: item.id } },
            icon: 'plane',
          },
          {
            key: 'add-budget-grouping',
            to: {
              name: 'CreateOrderBudgetGrouping',
              params: { order: item.id },
            },
            label: 'Add Budget Grouping',
            icon: 'budget-grouping',
          }
        )
      }

      if (canSeeElk()) {
        options.push({
          key: 'kibana',
          label: 'Go To Kibana',
          href: elkCampaignUrl(item),
          icon: 'kibana',
          attrs: {
            target: '_blank',
          },
        })
      }

      options.push({
        key: 'duplicate',
        label: 'Duplicate',
        icon: 'copy',
      })

      if (['Draft'].includes(item.campaignOrderStatusType)) {
        options.push({
          key: 'delete',
          label: 'Delete',
          icon: 'trash',
        })
      }

      if (['Converted'].includes(item.campaignOrderStatusType)) {
        options.push({
          key: 'archive',
          label: 'Archive',
          icon: 'archive',
        })
      }

      options.push({
        key: 'edit',
        label: 'Edit',
        icon: 'edit',
      })

      return options
    },
  },
  mutationsToWatch: [],
  moreActions: (item) => {
    if (!item) return []

    const actions = []

    if (!isDateInPast(item.endDate)) {
      actions.push(
        {
          key: 'create-flight',
          label: 'Create New Flight',
          to: { name: 'CreateOrderFlight', id: item.id },
          icon: 'plane',
        },
        {
          key: 'create-budget-grouping',
          label: 'Create New Budget Grouping',
          to: { name: 'CreateOrderBudgetGrouping', params: { order: item.id } },
          icon: 'budget-grouping',
        },
        {
          key: 'divider',
          divider: true,
        }
      )
    }

    actions.push(
      {
        key: 'duplicate',
        label: `Duplicate`,
        icon: 'copy',
      },
      {
        key: 'divider',
        divider: true,
      },
      {
        key: 'export-io',
        label: `Export IO to PDF`,
        icon: 'download',
      },
      {
        key: 'export-xls',
        label: `Export IO to Excel`,
        icon: 'download',
      }
    )

    if (['Converted'].includes(item.campaignOrderStatusType)) {
      actions.push({
        key: 'archive',
        label: `Archive`,
        icon: 'archive',
      })
    }

    if (['Draft'].includes(item.campaignOrderStatusType)) {
      actions.push(
        {
          key: 'divider-2',
          divider: true,
        },
        {
          key: 'delete',
          label: `Delete`,
          icon: 'trash',
          class: 'hover:tw-text-red',
        }
      )
    }

    return actions
  },
  handleStoreInput(module, path, value, valueKey = 'form') {
    store.dispatch(`${module}/handleInput`, { path, value, valueKey })
  },
  getDescription(item) {
    return `<div class="tw-flex tw-flex-col tw-space-y-4">
      <div class="tw-flex tw-flex-col">
        <div class="tw-font-semibold tw-text-sm">Name</div>
        <div class="tw-text-sm">${item.name}</div>
      </div>
      <div class="tw-flex tw-flex-col">
        <div class="tw-font-semibold tw-text-sm">Dates</div>
        <div class="tw-text-sm">${formatDate(item.startDate)} - ${
      item.endDate ? formatDate(item.endDate) : '-'
    }</div>
      </div>
      <div class="tw-grid tw-grid-cols-12 tw-gap-6">
        <div class="tw-col-span-6">
          <div class="tw-flex tw-flex-col">
            <div class="tw-font-semibold tw-text-sm">Ordered Impressions</div>
            <div class="tw-text-sm">${formatNumber(
              item.orderedImpressions
            )}</div>
          </div>
        </div>
        <div class="tw-col-span-6">
          <div class="tw-flex tw-flex-col">
            <div class="tw-font-semibold tw-text-sm">Gross CPM</div>
            <div class="tw-text-sm">${formatCurrency(
              item.grossCpm,
              item.currencyCode
            )}</div>
          </div>
        </div>
      </div>
    </div>`
  },
}

export default entity
